import { createSignal, createEffect, on, Show } from 'solid-js'
import { settingStore, toastStore, userStore } from '/web/store'
import { useGoogleReady } from '/web/shared/hooks'
import { getStrictForm } from '/web/shared/util'
import { usersApi } from '/web/store/data/user'
import './HentaiLoginModal.css'

type Mode = 'register' | 'login' | 'forgot'

const HentaiLoginModal = () => {
  const user = userStore()
  const cfg = settingStore()
  let refGoogle: any

  const [mode, setMode] = createSignal<Mode>(
    user.loginModalMode === 'login' ? 'login' : 'register'
  )
  const [showPassword, setShowPassword] = createSignal(false)
  const [loginUsername, setLoginUsername] = createSignal('')
  const [loginPassword, setLoginPassword] = createSignal('')

  const google = useGoogleReady()

  createEffect(() => {
    setMode(user.loginModalMode === 'login' ? 'login' : 'register')
  })

  createEffect(
    on(
      () => google(),
      () => {
        const win: any = window
        const api = win.google?.accounts?.id
        if (!api) return

        if (cfg.config.serverConfig?.googleClientId) {
          api.initialize({
            client_id: cfg.config.serverConfig?.googleClientId,
            callback: (result: any) => {
              userStore.handleGoogleCallback('login', result, () => {
                const redirect = userStore.getState().loginModalRedirect
                if (redirect) {
                  window.location.href = redirect
                } else {
                  window.location.reload()
                }
              })
            },
          })

          api.renderButton(refGoogle, {
            theme: 'filled_black',
            size: 'large',
            type: 'standard',
            text: mode() === 'register' ? 'signup_with' : 'signin_with',
          })
        }
      }
    )
  )

  const closeModal = () => {
    userStore.loginModal(false)
    setMode('register')
    setShowPassword(false)
  }

  const handleRegister = (evt: Event) => {
    evt.preventDefault()
    const { username, password, email } = getStrictForm(evt, {
      username: 'string',
      email: 'string',
      password: 'string',
    })

    if (!username || !password || !email) return

    try {
      userStore.register({ handle: username, username, password, email }, () => {
        const redirect = userStore.getState().loginModalRedirect
        if (redirect) {
          window.location.href = redirect + '?showTokens=1'
        } else {
          userStore.loginModal(false)
          window.location.reload()
        }
      })
    } catch (error: any) {
      toastStore.warn(error.message, 2)
    }
  }

  const handleLogin = () => {
    const username = loginUsername()
    const password = loginPassword()
    if (!username || !password) return

    userStore.login(username, password, () => {
      const redirect = userStore.getState().loginModalRedirect
      if (redirect) {
        window.location.href = redirect
      } else {
        userStore.loginModal(false)
        window.location.reload()
      }
    })
  }

  const handleForgotPassword = async (evt: Event) => {
    evt.preventDefault()
    const form = evt.target as HTMLFormElement
    const email = form.email.value
    try {
      const res = await usersApi.forgotPassword(email)
      if (res.error) {
        toastStore.error(`${res.error}`)
        return
      }
      toastStore.success(
        'If an account exists with that email, you will receive reset instructions shortly'
      )
      form.reset()
      setMode('login')
    } catch (error) {
      toastStore.error(`Error sending forgot password email: ${error}`)
    }
  }

  return (
    <Show when={user.showLogin && user.loginModalRedirect?.startsWith('/hentai')}>
      <div
        class="fixed inset-0 z-[200] overflow-y-auto"
        style="background: rgba(5,3,4,0.88); font-family: 'Inter', sans-serif"
        onClick={closeModal}
      >
        <div
          class="hentai-modal relative mx-auto my-4 w-[calc(100%-24px)] max-w-[480px] overflow-hidden rounded-[20px] md:my-8"
          style="background: linear-gradient(160deg, #1a0d10 0%, #0e0608 100%); border: 1px solid rgba(239,45,86,0.22); box-shadow: 0 0 80px rgba(239,45,86,0.18), 0 30px 80px rgba(0,0,0,0.7)"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal body */}
          <div class="relative z-[2] px-6 pb-8 pt-10 md:px-[38px] md:pb-9 md:pt-10">
            {/* Close button */}
            <button
              onClick={closeModal}
              class="absolute right-4 top-4 z-10 text-xl text-[#8a7377] transition-colors hover:text-white"
            >
              &#10005;
            </button>

            {/* Logo */}
            <div class="mb-5 text-center">
              <img
                src="/images/cosplayfans-logo.png"
                alt="CosplayFans"
                class="inline-block w-[75%] max-w-[220px]"
                style="filter: drop-shadow(0 4px 24px rgba(239,45,86,0.45))"
              />
            </div>

            {/* Tag */}
            <div class="mb-3.5 text-center">
              <div
                class="hentai-tag inline-flex items-center gap-2 rounded"
                style="background: rgba(239,45,86,0.10); border: 1px solid rgba(239,45,86,0.35); padding: 5px 10px; font-weight: 700; font-size: 10px; letter-spacing: 2.5px; color: #ef2d56; text-transform: uppercase"
              >
                AI-Powered Cosplay Universe
              </div>
            </div>

            {/* Title */}
            <Show when={mode() === 'register'}>
              <div
                class="mb-1 text-center text-[28px] leading-[1.05] md:text-[34px]"
                style="font-family: 'Poppins', sans-serif; font-weight: 900; letter-spacing: -0.02em"
              >
                Fantasy{' '}
                <span
                  style="background: linear-gradient(135deg, #ff4d70 0%, #ef2d56 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent"
                >
                  Unlocked
                </span>
              </div>
              <div
                class="mb-6 text-center text-sm font-medium"
                style="color: #8a7377"
              >
                Create your account & claim free tokens
              </div>
            </Show>

            <Show when={mode() === 'login'}>
              <div
                class="mb-1 text-center text-[28px] leading-[1.05] md:text-[34px]"
                style="font-family: 'Poppins', sans-serif; font-weight: 900; letter-spacing: -0.02em"
              >
                Welcome{' '}
                <span
                  style="background: linear-gradient(135deg, #ff4d70 0%, #ef2d56 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent"
                >
                  Back
                </span>
              </div>
              <div
                class="mb-6 text-center text-sm font-medium"
                style="color: #8a7377"
              >
                Log in to pick up where you left off
              </div>
            </Show>

            <Show when={mode() === 'forgot'}>
              <div
                class="mb-1 text-center text-[28px] leading-[1.05] md:text-[34px]"
                style="font-family: 'Poppins', sans-serif; font-weight: 900; letter-spacing: -0.02em"
              >
                Reset{' '}
                <span
                  style="background: linear-gradient(135deg, #ff4d70 0%, #ef2d56 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent"
                >
                  Password
                </span>
              </div>
              <div
                class="mb-6 text-center text-sm font-medium"
                style="color: #8a7377"
              >
                Enter your email and we'll send reset instructions
              </div>
            </Show>

            {/* Google button (not on forgot password) */}
            <Show when={mode() !== 'forgot'}>
              <div
                class="hentai-google-btn flex w-full cursor-pointer items-center justify-center gap-3 rounded-full text-sm font-bold text-white transition-all"
                style="padding: 14px 18px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.12)"
                ref={(ref) => {
                  refGoogle = ref
                }}
                id="g_id_onload"
                data-context={mode() === 'register' ? 'signup' : 'signin'}
                data-ux_mode="popup"
                data-login_uri={`${location.origin}/oauth/google`}
                data-itp_support="true"
              >
                <svg class="h-5 w-5 flex-shrink-0" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                Continue With Google
              </div>

              {/* Divider */}
              <div
                class="hentai-divider my-5 flex items-center gap-4"
              >
                <span
                  style="font-size: 11px; font-weight: 700; letter-spacing: 0.22em; text-transform: uppercase; color: #8a7377"
                >
                  Or
                </span>
              </div>
            </Show>

            {/* Sign Up Form */}
            <Show when={mode() === 'register'}>
              <form onSubmit={handleRegister}>
                <div class="mb-3.5">
                  <label
                    class="mb-2 block text-[11px] font-bold uppercase tracking-[0.12em] text-white"
                  >
                    Username
                  </label>
                  <input
                    name="username"
                    placeholder="Choose a username"
                    autocomplete="username"
                    class="hentai-input w-full rounded-xl text-sm font-medium text-white outline-none transition-all"
                    style="padding: 14px 18px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1)"
                  />
                </div>
                <div class="mb-3.5">
                  <label
                    class="mb-2 block text-[11px] font-bold uppercase tracking-[0.12em] text-white"
                  >
                    Email Address
                  </label>
                  <input
                    name="email"
                    type="email"
                    placeholder="Enter your email"
                    autocomplete="email"
                    class="hentai-input w-full rounded-xl text-sm font-medium text-white outline-none transition-all"
                    style="padding: 14px 18px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1)"
                  />
                </div>
                <div class="mb-3.5">
                  <label
                    class="mb-2 block text-[11px] font-bold uppercase tracking-[0.12em] text-white"
                  >
                    Password
                  </label>
                  <div class="relative">
                    <input
                      name="password"
                      type={showPassword() ? 'text' : 'password'}
                      placeholder="Create a password"
                      autocomplete="new-password"
                      class="hentai-input w-full rounded-xl text-sm font-medium text-white outline-none transition-all"
                      style="padding: 14px 18px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1)"
                    />
                    <button
                      type="button"
                      class="hentai-pw-toggle absolute right-3.5 top-1/2 flex -translate-y-1/2 items-center border-none bg-transparent p-1 transition-colors"
                      style="color: #8a7377; cursor: pointer"
                      onClick={() => setShowPassword(!showPassword())}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  class="hentai-signup-btn mt-3 w-full cursor-pointer rounded-full border-none text-[14px] font-extrabold uppercase tracking-[0.08em] text-white md:text-[15px]"
                  style="padding: 17px; background: linear-gradient(90deg, #ff4d70 0%, #ef2d56 55%, #c71f3e 100%); box-shadow: 0 8px 24px rgba(239,45,86,0.4), inset 0 1px 0 rgba(255,255,255,0.25); transition: all 0.25s"
                >
                  Claim Free Tokens & Join &rarr;
                </button>
              </form>

              <div
                class="mt-4 text-center text-xs leading-relaxed"
                style="color: #8a7377"
              >
                By registering you accept our{' '}
                <a
                  href="/privacy-policy/"
                  class="font-semibold no-underline hover:underline"
                  style="color: #ff4d70"
                >
                  Privacy Policy
                </a>
              </div>

              <div
                class="mt-4 pt-4 text-center text-[13px] font-medium"
                style="color: #8a7377; border-top: 1px solid rgba(239,45,86,0.12)"
              >
                Already have an account?{' '}
                <a
                  href="#"
                  class="no-underline hover:underline"
                  style="color: #ef2d56; font-weight: 800; text-transform: uppercase; letter-spacing: 0.08em; font-size: 12px"
                  onClick={(e) => {
                    e.preventDefault()
                    setMode('login')
                  }}
                >
                  Log In
                </a>
              </div>
            </Show>

            {/* Login Form */}
            <Show when={mode() === 'login'}>
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  handleLogin()
                }}
              >
                <div class="mb-3.5">
                  <label
                    class="mb-2 block text-[11px] font-bold uppercase tracking-[0.12em] text-white"
                  >
                    Username Or Email
                  </label>
                  <input
                    name="username"
                    placeholder="Enter username or email"
                    autocomplete="username"
                    class="hentai-input w-full rounded-xl text-sm font-medium text-white outline-none transition-all"
                    style="padding: 14px 18px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1)"
                    onInput={(ev) => setLoginUsername(ev.currentTarget.value)}
                  />
                </div>
                <div class="mb-3.5">
                  <label
                    class="mb-2 block text-[11px] font-bold uppercase tracking-[0.12em] text-white"
                  >
                    Password
                  </label>
                  <div class="relative">
                    <input
                      name="password"
                      type={showPassword() ? 'text' : 'password'}
                      placeholder="Enter password"
                      autocomplete="current-password"
                      class="hentai-input w-full rounded-xl text-sm font-medium text-white outline-none transition-all"
                      style="padding: 14px 18px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1)"
                      onInput={(ev) => setLoginPassword(ev.currentTarget.value)}
                      onKeyDown={(ev) => {
                        if (ev.key === 'Enter') {
                          ev.preventDefault()
                          handleLogin()
                        }
                      }}
                    />
                    <button
                      type="button"
                      class="hentai-pw-toggle absolute right-3.5 top-1/2 flex -translate-y-1/2 items-center border-none bg-transparent p-1 transition-colors"
                      style="color: #8a7377; cursor: pointer"
                      onClick={() => setShowPassword(!showPassword())}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div class="flex items-center justify-end">
                  <a
                    href="#"
                    class="text-xs no-underline hover:underline"
                    style="color: #ff4d70"
                    onClick={(e) => {
                      e.preventDefault()
                      setMode('forgot')
                    }}
                  >
                    Forgot Password?
                  </a>
                </div>

                <button
                  type="submit"
                  class="hentai-signup-btn mt-3 w-full cursor-pointer rounded-full border-none text-[14px] font-extrabold uppercase tracking-[0.08em] text-white md:text-[15px]"
                  style="padding: 17px; background: linear-gradient(90deg, #ff4d70 0%, #ef2d56 55%, #c71f3e 100%); box-shadow: 0 8px 24px rgba(239,45,86,0.4), inset 0 1px 0 rgba(255,255,255,0.25); transition: all 0.25s"
                >
                  Log In &rarr;
                </button>
              </form>

              <div
                class="mt-4 pt-4 text-center text-[13px] font-medium"
                style="color: #8a7377; border-top: 1px solid rgba(239,45,86,0.12)"
              >
                Don't have an account?{' '}
                <a
                  href="#"
                  class="no-underline hover:underline"
                  style="color: #ef2d56; font-weight: 800; text-transform: uppercase; letter-spacing: 0.08em; font-size: 12px"
                  onClick={(e) => {
                    e.preventDefault()
                    setMode('register')
                  }}
                >
                  Sign Up
                </a>
              </div>
            </Show>

            {/* Forgot Password Form */}
            <Show when={mode() === 'forgot'}>
              <form onSubmit={handleForgotPassword}>
                <div class="mb-3.5">
                  <label
                    class="mb-2 block text-[11px] font-bold uppercase tracking-[0.12em] text-white"
                  >
                    Email Address
                  </label>
                  <input
                    name="email"
                    type="email"
                    placeholder="Enter your email"
                    autocomplete="email"
                    required
                    class="hentai-input w-full rounded-xl text-sm font-medium text-white outline-none transition-all"
                    style="padding: 14px 18px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1)"
                  />
                </div>

                <button
                  type="submit"
                  class="hentai-signup-btn mt-3 w-full cursor-pointer rounded-full border-none text-[14px] font-extrabold uppercase tracking-[0.08em] text-white md:text-[15px]"
                  style="padding: 17px; background: linear-gradient(90deg, #ff4d70 0%, #ef2d56 55%, #c71f3e 100%); box-shadow: 0 8px 24px rgba(239,45,86,0.4), inset 0 1px 0 rgba(255,255,255,0.25); transition: all 0.25s"
                >
                  Reset Password &rarr;
                </button>
              </form>

              <div
                class="mt-4 pt-4 text-center text-[13px] font-medium"
                style="color: #8a7377; border-top: 1px solid rgba(239,45,86,0.12)"
              >
                Remember your password?{' '}
                <a
                  href="#"
                  class="no-underline hover:underline"
                  style="color: #ef2d56; font-weight: 800; text-transform: uppercase; letter-spacing: 0.08em; font-size: 12px"
                  onClick={(e) => {
                    e.preventDefault()
                    setMode('login')
                  }}
                >
                  Log In
                </a>
              </div>
            </Show>
          </div>
        </div>
      </div>
    </Show>
  )
}

export default HentaiLoginModal
